import { useEffect, useState } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Loader2, BadgeCheck, Store, ExternalLink } from 'lucide-react';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import { fetchMerchantItem } from '../hooks/useMerchantMarket';
import PriceDisplay from '../components/marketplace/PriceDisplay';
import ContactActions from '../components/marketplace/ContactActions';
import StoreUidBadge from '../components/marketplace/StoreUidBadge';
import SafetyTips from '../components/marketplace/SafetyTips';
import PlateVisual from '../components/plates/PlateVisual';
import { getDisplayPlateNumber } from '../utils/digitMask';

const EMIRATES = {
  dubai:          { ar: 'دبي',        en: 'Dubai' },
  abu_dhabi:      { ar: 'أبوظبي',     en: 'Abu Dhabi' },
  sharjah:        { ar: 'الشارقة',    en: 'Sharjah' },
  ajman:          { ar: 'عجمان',      en: 'Ajman' },
  ras_al_khaimah: { ar: 'رأس الخيمة', en: 'Ras Al Khaimah' },
  umm_al_quwain:  { ar: 'أم القيوين', en: 'Umm Al Quwain' },
  fujairah:       { ar: 'الفجيرة',    en: 'Fujairah' },
};

export default function PlateDetailsPage() {
  const { id } = useParams();
  const location = useLocation();
  const cachedStore = location.state?.store || null;
  const cachedItem = location.state?.item || null;
  const { language } = useLanguage();
  const { isDarkMode } = useTheme();
  const isAr = language === 'ar';
  const [data, setData] = useState({ item: cachedItem, store: cachedStore });
  const [loading, setLoading] = useState(!cachedItem);
  const [error, setError] = useState(null);
  const BackIcon = isAr ? ArrowRight : ArrowLeft;

  useEffect(() => {
    let mounted = true;
    fetchMerchantItem('car_plates', id, cachedStore)
      .then((res) => {
        if (mounted) setData((prev) => ({ item: res.item || prev.item, store: res.store || prev.store }));
      })
      .catch((e) => {
        if (mounted && !cachedItem) setError(e.message);
      })
      .finally(() => setLoading(false));
    return () => { mounted = false; };
  }, [id]);

  if (loading)
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#080808]' : 'bg-[#F6F8FB]'}`}>
        <Loader2 className="h-8 w-8 animate-spin text-[#F26522]" />
      </div>
    );
  if (error || !data.item)
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#080808]' : 'bg-[#F6F8FB]'}`}>
        <p className="text-red-500">{error || 'Not found'}</p>
      </div>
    );

  const { item, store } = data;
  const em = EMIRATES[item.emirate];
  const shownNumber = getDisplayPlateNumber(item) || item.plateNumber;
  const title = isAr
    ? `لوحة ${item.plateCode || ''} ${shownNumber || ''}`
    : `Plate ${item.plateCode || ''} ${shownNumber || ''}`;

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#080808]' : 'bg-[#F6F8FB]'}`} dir={isAr ? 'rtl' : 'ltr'}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link
          to="/plates"
          className={`inline-flex items-center gap-1.5 text-sm mb-6 hover:underline ${
            isDarkMode ? 'text-slate-300' : 'text-slate-600'
          }`}
          data-testid="back-link"
        >
          <BackIcon className="h-4 w-4" />
          {isAr ? 'العودة إلى اللوحات' : 'Back to Plates'}
        </Link>

        {/* ═══════ Plate Hero Card ═══════ */}
        <div
          className={`relative rounded-3xl p-8 overflow-hidden ${
            isDarkMode ? 'bg-[#111] border border-white/10' : 'bg-white border border-slate-200 shadow-xl shadow-slate-900/5'
          }`}
        >
          <div
            className="absolute inset-x-0 top-0 h-1.5"
            style={{ background: `linear-gradient(90deg, #F26522, #E4002B55)` }}
          />
          <div className="absolute -top-24 -end-24 w-80 h-80 rounded-full blur-3xl opacity-25" style={{ background: '#F26522' }} />

          <div className="relative">
            {/* Emirate + type pills */}
            <div className="flex justify-center flex-wrap items-center gap-2 mb-6">
              <span
                className={`px-3 py-1 rounded-full text-xs font-bold ${
                  isDarkMode ? 'bg-white/10 text-white border border-white/10' : 'bg-slate-900 text-white'
                }`}
              >
                {em ? (isAr ? em.ar : em.en) : item.emirate}
              </span>
              {item.plateType && (
                <span
                  className={`px-3 py-1 rounded-full text-xs font-bold ${
                    isDarkMode ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {item.plateType}
                </span>
              )}
            </div>

            {/* THE plate — SAME PlateVisual as the listing page */}
            <div className="flex items-center justify-center mb-8">
              <PlateVisual
                emirate={item.emirate}
                category={item.plateType}
                code={item.plateCode}
                number={shownNumber}
                width={380}
                showCodeOnPlate={item.show_on_plate}
              />
            </div>

            {/* Price */}
            <div className="flex justify-center">
              <PriceDisplay item={item} locale={isAr ? 'ar' : 'en'} size="lg" />
            </div>

            {/* Description */}
            {item.description && (
              <div
                className={`mt-6 rounded-xl p-4 text-sm text-center ${
                  isDarkMode ? 'bg-white/5 text-slate-300' : 'bg-slate-50 text-slate-700'
                }`}
              >
                {item.description}
              </div>
            )}
          </div>
        </div>

        {/* ═══════ Store Card (prominent, with Visit button) ═══════ */}
        {store?.id && (
          <div
            className={`mt-6 rounded-2xl p-5 ${
              isDarkMode ? 'bg-[#111] border border-white/10' : 'bg-white border border-slate-200 shadow-sm'
            }`}
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                {store.logo ? (
                  <img src={store.logo} alt="" className="w-12 h-12 rounded-xl object-cover shrink-0" />
                ) : (
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${
                      isDarkMode ? 'bg-white/5' : 'bg-slate-100'
                    }`}
                  >
                    <Store className="h-6 w-6 text-slate-400" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className={`font-bold truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                      {isAr ? (store.name || store.nameEn) : (store.nameEn || store.name)}
                    </span>
                    {store.verified && <BadgeCheck className="h-4 w-4 text-[#1E9BD9] shrink-0" />}
                  </div>
                  <StoreUidBadge store={store} locale={isAr ? 'ar' : 'en'} className="mt-1" />
                </div>
              </div>
              <Link
                to={`/store/${store.id}`}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-all hover:-translate-y-0.5 ${
                  isDarkMode ? 'bg-white text-slate-900 hover:bg-slate-100' : 'bg-slate-900 text-white hover:bg-slate-800'
                }`}
                data-testid="visit-store-btn"
              >
                <Store className="h-4 w-4" />
                {isAr ? 'زيارة المتجر' : 'Visit store'}
                <ExternalLink className="h-3.5 w-3.5 opacity-70" />
              </Link>
            </div>
          </div>
        )}

        {/* ═══════ Safety Tips ═══════ */}
        <div className="mt-6">
          <SafetyTips category="plates" isAr={isAr} isDarkMode={isDarkMode} />
        </div>

        {/* ═══════ Contact ═══════ */}
        <div
          className={`mt-6 rounded-2xl p-5 ${
            isDarkMode ? 'bg-[#111] border border-white/10' : 'bg-white border border-slate-200 shadow-sm'
          }`}
        >
          <h3 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            {isAr ? 'التواصل مع البائع' : 'Contact seller'}
          </h3>
          <ContactActions store={store} itemTitle={title} locale={isAr ? 'ar' : 'en'} />
        </div>
      </div>
    </div>
  );
}
